import React, { useCallback, useEffect, useState } from "react";
import { FiCrosshair, FiEdit2, FiMapPin, FiPlus, FiPower, FiX } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatInteger(value) {
  return Number(value || 0).toLocaleString("es-ES");
}

export default function Outlets() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [outlets, setOutlets] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [modalMode, setModalMode] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [form, setForm] = useState({
    display_name: "",
    is_active: true,
    guest_order_latitude: "",
    guest_order_longitude: "",
    guest_order_radius_meters: 150,
    guest_order_session_minutes: 10,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const limit = 50;

  const loadOutlets = useCallback(async (nextOffset = 0) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/pos/outlets", {
        params: {
          business_unit_id: businessUnitId,
          q: debouncedSearchQuery,
          include_inactive: true,
          limit,
          offset: nextOffset,
        },
      });
      setOutlets(res.data?.rows || []);
      setCount(Number(res.data?.count || 0));
      setOffset(nextOffset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load POS outlets.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, debouncedSearchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    loadOutlets(0);
  }, [loadOutlets]);

  const openCreate = () => {
    setSelectedOutlet(null);
    setForm({ display_name: "", is_active: true, guest_order_latitude: "", guest_order_longitude: "", guest_order_radius_meters: 150, guest_order_session_minutes: 10 });
    setModalMode("create");
    setError("");
  };

  const openEdit = (outlet) => {
    setSelectedOutlet(outlet);
    setForm({
      display_name: outlet.display_name || "",
      is_active: outlet.is_active !== false,
      guest_order_latitude: outlet.guest_order_latitude ?? "",
      guest_order_longitude: outlet.guest_order_longitude ?? "",
      guest_order_radius_meters: outlet.guest_order_radius_meters || 150,
      guest_order_session_minutes: outlet.guest_order_session_minutes || 10,
    });
    setModalMode("edit");
    setError("");
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedOutlet(null);
    setForm({ display_name: "", is_active: true, guest_order_latitude: "", guest_order_longitude: "", guest_order_radius_meters: 150, guest_order_session_minutes: 10 });
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("This browser cannot provide the current location.");
      return;
    }
    setLocating(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((current) => ({
          ...current,
          guest_order_latitude: position.coords.latitude.toFixed(7),
          guest_order_longitude: position.coords.longitude.toFixed(7),
        }));
        setLocating(false);
      },
      (locationError) => {
        setError(locationError?.message || "Could not read the current location.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const saveOutlet = async () => {
    const displayName = form.display_name.trim();
    if (!displayName) {
      setError("Outlet name is required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      if (modalMode === "edit" && selectedOutlet) {
        await axios.patch(`/api/pos/outlets/${selectedOutlet.id}`, {
          business_unit_id: businessUnitId,
          display_name: displayName,
          is_active: form.is_active,
          guest_order_latitude: form.guest_order_latitude === "" ? null : form.guest_order_latitude,
          guest_order_longitude: form.guest_order_longitude === "" ? null : form.guest_order_longitude,
          guest_order_radius_meters: Number(form.guest_order_radius_meters),
          guest_order_session_minutes: Number(form.guest_order_session_minutes),
        });
        setMessage(`${displayName} updated.`);
        await loadOutlets(offset);
      } else {
        await axios.post("/api/pos/outlets", {
          business_unit_id: businessUnitId,
          display_name: displayName,
          is_active: form.is_active,
          guest_order_latitude: form.guest_order_latitude === "" ? null : form.guest_order_latitude,
          guest_order_longitude: form.guest_order_longitude === "" ? null : form.guest_order_longitude,
          guest_order_radius_meters: Number(form.guest_order_radius_meters),
          guest_order_session_minutes: Number(form.guest_order_session_minutes),
        });
        setMessage(`${displayName} created.`);
        await loadOutlets(0);
      }
      closeModal();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save POS outlet.");
    } finally {
      setSaving(false);
    }
  };

  const toggleOutlet = async (outlet) => {
    try {
      setActionId(outlet.id);
      setError("");
      setMessage("");
      await axios.patch(`/api/pos/outlets/${outlet.id}`, {
        business_unit_id: businessUnitId,
        is_active: outlet.is_active === false,
      });
      setMessage(`${outlet.display_name} ${outlet.is_active === false ? "activated" : "deactivated"}.`);
      await loadOutlets(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update POS outlet.");
    } finally {
      setActionId(null);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
  };

  return (
    <div className="reservations-container rm-page">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Outlets</h3>
            <div className="rm-rates-subtitle">
              {loading ? "Loading outlets..." : `${formatInteger(count)} POS outlets`}
            </div>
          </div>
          <div className="rm-topActions">
            <input
              className="rm-searchInput"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search outlet"
            />
            <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={clearFilters}>
              <FiX /> Clear
            </button>
            <button type="button" className="rm-topBtn rm-topBtn--green" onClick={openCreate}>
              <FiPlus /> Add outlet
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {message ? <div className="posInfoBanner">{message}</div> : null}

      <div className="rm-rates-card">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">Outlet directory</h3>
          <div className="rm-rates-subtitle">Imported outlets are added automatically from POS sales files.</div>
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {loading ? "Loading outlets..." : `Showing ${count ? offset + 1 : 0}-${Math.min(count, offset + outlets.length)} of ${formatInteger(count)}`}
          </small>
          <div className="rm-topActions">
            <button type="button" className="rm-topBtn" disabled={offset === 0} onClick={() => loadOutlets(Math.max(0, offset - limit))}>
              Previous
            </button>
            <button type="button" className="rm-topBtn" disabled={offset + limit >= count} onClick={() => loadOutlets(offset + limit)}>
              Next
            </button>
          </div>
        </div>

        <div className="rm-rates-table-wrap">
          <table className="rm-rates-table" style={{ "--rm-col1-width": "250px", "--rm-table-min-width": "1570px" }}>
            <colgroup>
              <col style={{ width: "250px" }} />
              <col style={{ width: "230px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "190px" }} />
              <col style={{ width: "200px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "250px" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Outlet</span><span className="res-thSub">Display name</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Source name</span><span className="res-thSub">Original POS value</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Source</span><span className="res-thSub">Created by</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Tickets</span><span className="res-thSub">Linked sales</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Catalogs</span><span className="res-thSub">Linked menus</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Devices</span><span className="res-thSub">Assigned</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Last seen</span><span className="res-thSub">Latest import</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">QR ordering</span><span className="res-thSub">Location · session</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Status</span><span className="res-thSub">Availability</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Actions</span><span className="res-thSub">Setup</span></div></th>
              </tr>
            </thead>
            <tbody>
              {outlets.map((outlet) => (
                <tr key={outlet.id}>
                  <td><strong className="posDeviceName"><FiMapPin /> {outlet.display_name}</strong></td>
                  <td>{outlet.source_name || "-"}</td>
                  <td>{outlet.source_system === "pos_setup" ? "Setup" : "POS import"}</td>
                  <td>{formatInteger(outlet.checks_count)}</td>
                  <td>{formatInteger(outlet.catalogs_count)}</td>
                  <td>{formatInteger(outlet.devices_count)}</td>
                  <td>{formatDate(outlet.last_seen_at)}</td>
                  <td>
                    <span className={`rm-chip ${outlet.guest_order_location_ready ? "rm-chip--ok" : "rm-chip--off"}`}>
                      {outlet.guest_order_location_ready ? `${formatInteger(outlet.guest_order_radius_meters)} m · ${outlet.guest_order_session_minutes} min` : "Not configured"}
                    </span>
                  </td>
                  <td>
                    <span className={`rm-chip ${outlet.is_active ? "rm-chip--ok" : "rm-chip--off"}`}>
                      {outlet.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="posDeviceActions">
                      <button type="button" className="rm-topBtn" onClick={() => openEdit(outlet)} disabled={actionId === outlet.id}>
                        <FiEdit2 /> Edit
                      </button>
                      <button
                        type="button"
                        className={`rm-topBtn ${outlet.is_active ? "rm-topBtn--amber" : "rm-topBtn--green"}`}
                        onClick={() => toggleOutlet(outlet)}
                        disabled={actionId === outlet.id}
                      >
                        <FiPower /> {outlet.is_active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!outlets.length ? <tr><td colSpan="10" className="posMuted">No POS outlets found.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>

      {modalMode ? (
        <div className="posModalOverlay" role="presentation">
          <div className="posTicketModal posProductModal" role="dialog" aria-modal="true" aria-label="POS outlet setup">
            <div className="posTicketModalHeader">
              <div>
                <h3 className="rm-rates-title">{modalMode === "edit" ? "Edit outlet" : "Add outlet"}</h3>
                <div className="rm-rates-subtitle">
                  {selectedOutlet?.source_name ? `Source: ${selectedOutlet.source_name}` : "Create an outlet before assigning catalogs or devices."}
                </div>
              </div>
              <button type="button" className="res-filterCloseBtn" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <div className="posProductModalBody">
              <div className="posModalFormGrid">
                <label className="posModalField posModalField--wide">
                  <span>Display name</span>
                  <input
                    className="posTextInput"
                    value={form.display_name}
                    onChange={(event) => setForm((current) => ({ ...current, display_name: event.target.value }))}
                    placeholder="Rooftop"
                  />
                </label>
                <label className="posCatalogToggle">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(event) => setForm((current) => ({ ...current, is_active: event.target.checked }))}
                  />
                  Active outlet
                </label>
                <div className="posModalField posModalField--wide posOutletLocationHeader">
                  <span>Guest ordering location</span>
                  <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={useCurrentLocation} disabled={locating}>
                    <FiCrosshair /> {locating ? "Locating" : "Use current location"}
                  </button>
                </div>
                <label className="posModalField">
                  <span>Latitude</span>
                  <input className="posTextInput" inputMode="decimal" value={form.guest_order_latitude} onChange={(event) => setForm((current) => ({ ...current, guest_order_latitude: event.target.value }))} placeholder="41.3874000" />
                </label>
                <label className="posModalField">
                  <span>Longitude</span>
                  <input className="posTextInput" inputMode="decimal" value={form.guest_order_longitude} onChange={(event) => setForm((current) => ({ ...current, guest_order_longitude: event.target.value }))} placeholder="2.1686000" />
                </label>
                <label className="posModalField">
                  <span>Allowed radius (metres)</span>
                  <input className="posTextInput" type="number" min="50" max="5000" step="10" value={form.guest_order_radius_meters} onChange={(event) => setForm((current) => ({ ...current, guest_order_radius_meters: event.target.value }))} />
                </label>
                <label className="posModalField">
                  <span>Order window (minutes)</span>
                  <input className="posTextInput" type="number" min="5" max="60" step="1" value={form.guest_order_session_minutes} onChange={(event) => setForm((current) => ({ ...current, guest_order_session_minutes: event.target.value }))} />
                </label>
              </div>
            </div>
            <div className="posProductModalFooter">
              <button type="button" className="rm-topBtn" onClick={closeModal} disabled={saving}>Cancel</button>
              <button type="button" className="rm-topBtn rm-topBtn--green" onClick={saveOutlet} disabled={saving}>
                {saving ? "Saving" : "Save outlet"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
